import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, Suspense, useLayoutEffect, useRef, useState, } from 'react';
import { Typography, Box } from '@mui/material';
import { lazy } from 'react';
import rehypeSanitize from 'rehype-sanitize';
import { styled } from '@mui/material/styles';
import './styles/markdown-editor.css';
import './styles/markdown-preview.css';
// Commands are built inside the lazy factory: a static value import of
// @uiw/react-md-editor would pull the whole editor into the main chunk.
const MDEditor = lazy(async () => {
    const commands = await import('@uiw/react-md-editor');
    // md-editor's heading command with a German label; the `icon` doubles as
    // the label in md-editor's dropdown.
    const headingLevel = (command, label, fontSize) => ({
        ...command,
        icon: _jsx(Box, { sx: { fontSize, fontWeight: 600 }, children: label }),
        buttonProps: { 'aria-label': label, title: label },
    });
    const commandMapping = {
        bold: commands.bold,
        italic: commands.italic,
        strikethrough: commands.strikethrough,
        hr: commands.hr,
        titles: commands.group([
            commands.heading1,
            commands.heading2,
            commands.heading3,
            commands.heading4,
            commands.heading5,
            commands.heading6,
        ], {
            name: 'title',
            groupName: 'title',
            buttonProps: { 'aria-label': 'Insert title' },
        }),
        divider: commands.divider,
        link: commands.link,
        quote: commands.quote,
        code: commands.code,
        codeBlock: commands.codeBlock,
        unorderedList: commands.unorderedListCommand,
        orderedList: commands.orderedListCommand,
        checkedList: commands.checkedListCommand,
        image: commands.image,
        // Three levels with German labels. Opt-in via `commands`, never part
        // of the default toolbar.
        heading: commands.group([
            headingLevel(commands.heading1, 'Überschrift 1', 15),
            headingLevel(commands.heading2, 'Überschrift 2', 14),
            headingLevel(commands.heading3, 'Überschrift 3', 13),
        ], {
            name: 'heading',
            groupName: 'heading',
            icon: commands.heading.icon,
            buttonProps: {
                'aria-label': 'Überschrift',
                title: 'Überschrift',
            },
        }),
    };
    const Editor = commands.default;
    return {
        default: ({ commandKeys, excludeCommands, ...props }) => (_jsx(Editor, { ...props, commands: commandKeys
                .filter((key) => !excludeCommands.includes(key))
                .map((key) => commandMapping[key]) })),
    };
});
// Our custom `textarea` replaces md-editor's highlighted <pre> as well, and
// that <pre> was what gave the editor its height. Without it the absolutely
// positioned textarea has none, so size it to its content ourselves.
const AutoGrowTextarea = forwardRef(function AutoGrowTextarea(props, ref) {
    const innerRef = useRef(null);
    const setRefs = (el) => {
        innerRef.current = el;
        if (typeof ref === 'function')
            ref(el);
        else if (ref)
            ref.current = el;
    };
    useLayoutEffect(() => {
        const el = innerRef.current;
        if (!el)
            return;
        const fit = () => {
            el.style.height = 'auto';
            el.style.height = `${el.scrollHeight}px`;
        };
        fit();
        // Wrapping, and so the height, changes with the width.
        let width = el.clientWidth;
        const observer = new ResizeObserver(() => {
            if (el.clientWidth === width)
                return;
            width = el.clientWidth;
            fit();
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [props.value]);
    return (_jsx("textarea", { ...props, ref: setRefs, style: { ...props.style, overflow: 'hidden' } }));
});
const DEFAULT_COMMANDS = [
    'bold',
    'italic',
    'strikethrough',
    'hr',
    'titles',
    'divider',
    'link',
    'quote',
    'code',
    'codeBlock',
    'unorderedList',
    'orderedList',
    'checkedList',
    'image',
];
const MarkdownInputComponent = ({ label, value, defaultValue, onBlur, onChange, name, commands: commandKeys = DEFAULT_COMMANDS, excludeCommands = [], maxLength, autoGrow = false, }) => {
    const [inputValue, setInputValue] = useState(value);
    const [oldPropsValue, setOldPropsValue] = useState(value);
    if (oldPropsValue !== value) {
        setOldPropsValue(value);
        setInputValue(value);
    }
    if (value && !onChange) {
        console.warn('If you provide a value please also provide an onChange handler to fully control the component');
    }
    const handleChange = (val, event) => {
        setInputValue(val);
        onChange?.(val || '', event);
    };
    const handleBlur = (e) => {
        onBlur?.(inputValue || '', e);
    };
    return (_jsxs(Box, { sx: {
            width: '100%',
            ...(autoGrow && {
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 auto',
                minHeight: 0,
                '& .w-md-editor': { flex: '1 1 auto', minHeight: 0 },
                // Size from the textarea, shrink to the parent, and
                // scroll inside the editor pane so the toolbar and the
                // preview stay put.
                '& .w-md-editor-content': {
                    display: 'flex',
                    height: 'auto',
                    flex: '1 1 auto',
                    minHeight: 160,
                    overflow: 'hidden',
                },
                // A row flex stretches the pane to the (shrunk) content
                // height, which `height: 100%` on an auto-height box can't.
                // The editor pane (`-input` and `-area` are one element).
                // No grow, so its width stays md-editor's: 50% next to
                // the live preview.
                '& .w-md-editor-input': {
                    flex: '0 1 auto',
                    height: 'auto',
                    minHeight: 0,
                    overflow: 'auto',
                },
                // When the editor is taller than the text (a parent with
                // a fixed height), the textarea still fills the pane so a
                // click anywhere in it puts the cursor in.
                '& .w-md-editor-text': {
                    height: 'auto',
                    // md-editor sets an inline `min-height: 100px` here.
                    minHeight: '100% !important',
                    display: 'flex',
                    flexDirection: 'column',
                },
                '& .w-md-editor-text-input': {
                    position: 'relative',
                    flex: '1 0 auto',
                },
            }),
        }, "data-color-mode": 'light', children: [label && (_jsx(Typography, { sx: {
                    color: 'text.secondary',
                    fontWeight: 400,
                    fontSize: '0.90rem',
                    letterSpacing: '0.00938em',
                    display: 'block',
                    marginBottom: '8px',
                    transformOrigin: 'top left',
                }, component: 'label', children: label })), _jsx(Suspense, { fallback: _jsx(Box, { children: "Loading editor..." }), children: _jsx(MDEditor, { className: 'wmde-markdown', value: inputValue, height: autoGrow ? 'auto' : undefined, defaultValue: defaultValue, onChange: handleChange, onBlur: handleBlur, commandKeys: commandKeys, excludeCommands: excludeCommands, previewOptions: {
                        rehypePlugins: [[rehypeSanitize]],
                    }, components: {
                        textarea: (props) => {
                            const Textarea = autoGrow
                                ? AutoGrowTextarea
                                : 'textarea';
                            return (_jsx(Textarea, { ...props, name: name, maxLength: maxLength }));
                        },
                    }, visibleDragbar: false }) })] }));
};
export const MarkdownInput = styled(MarkdownInputComponent)(({ theme }) => ({
    '& .wmde-markdown': {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
    },
}), { name: 'MarkdownInput' });
